import { useChartColors } from "./chartColors";

/* --------------------------------- Column -------------------------------- */

export interface ColumnGroup {
  key: string;
  label: string;
  /** One entry per series, same order as `series`. */
  values: number[];
}

export interface ColumnSeries {
  key: string;
  label: string;
  color: string;
}

/**
 * Grouped columns for a trend. Both series share ONE scale — plotting two
 * different units against two axes lets the chart imply any relationship the
 * author likes, so it is never done here.
 */
export const ColumnChart = ({
  groups,
  series,
  formatValue,
}: {
  groups: ColumnGroup[];
  series: ColumnSeries[];
  formatValue?: (value: number) => string;
}) => {
  const max = Math.max(1, ...groups.flatMap((group) => group.values));
  const chartColors = useChartColors();

  return (
    <div className="column-chart">
      {groups.map((group) => (
        <div className="column-chart__group" key={group.key}>
          <div className="column-chart__bars">
            {group.values.map((value, index) => (
              <div
                className="column-chart__bar"
                key={series[index]?.key ?? index}
                style={{
                  height: `${(value / max) * 100}%`,
                  background: series[index]?.color ?? chartColors.other,
                }}
                title={`${series[index]?.label ?? ""}: ${
                  formatValue ? formatValue(value) : value
                }`}
              />
            ))}
          </div>
          <span className="column-chart__label">{group.label}</span>
        </div>
      ))}
    </div>
  );
};

/* --------------------------------- Donut --------------------------------- */

export interface DonutSlice {
  key: string;
  label: string;
  value: number;
  color: string;
}

/** Part-to-whole with a total worth putting in the middle. */
export const DonutChart = ({
  slices,
  centerValue,
  centerLabel,
}: {
  slices: DonutSlice[];
  centerValue?: string;
  centerLabel?: string;
}) => {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);

  let cursor = 0;
  const stops = slices
    .filter((slice) => slice.value > 0)
    .map((slice) => {
      const start = (cursor / total) * 360;
      cursor += slice.value;
      const end = (cursor / total) * 360;
      return `${slice.color} ${start}deg ${end}deg`;
    })
    .join(", ");

  return (
    <div className="donut">
      <div
        className="donut__ring"
        style={{
          background: total > 0 ? `conic-gradient(${stops})` : "var(--border)",
        }}
      >
        <div className="donut__hole">
          {centerValue ? <span className="donut__value">{centerValue}</span> : null}
          {centerLabel ? <span className="donut__label">{centerLabel}</span> : null}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------- StackedBar ------------------------------ */

export interface StackSegment {
  key: string;
  value: number;
  color: string;
}

/** A single horizontal part-to-whole bar. */
export const StackedBar = ({ segments }: { segments: StackSegment[] }) => {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0) || 1;

  return (
    <div className="stacked-bar">
      {segments
        .filter((segment) => segment.value > 0)
        .map((segment) => (
          <span
            className="stacked-bar__seg"
            key={segment.key}
            style={{
              width: `${(segment.value / total) * 100}%`,
              background: segment.color,
            }}
          />
        ))}
    </div>
  );
};

/* --------------------------------- BarList ------------------------------- */

export interface BarItem {
  key: string;
  label: string;
  value: number;
}

/** Ranked horizontal bars. One series, so one hue. */
export const BarList = ({
  items,
  formatValue,
}: {
  items: BarItem[];
  formatValue?: (value: number) => string;
}) => {
  const max = Math.max(1, ...items.map((item) => item.value));
  const chartColors = useChartColors();

  return (
    <ul className="bar-list">
      {items.map((item) => (
        <li className="bar-list__row" key={item.key}>
          <div className="bar-list__head">
            <span className="bar-list__label" title={item.label}>
              {item.label}
            </span>
            <span className="bar-list__value">
              {formatValue ? formatValue(item.value) : item.value.toLocaleString()}
            </span>
          </div>
          <div className="bar-list__track">
            <span
              className="bar-list__fill"
              style={{
                width: `${(item.value / max) * 100}%`,
                background: chartColors.sequential,
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
};

/* -------------------------------- BarChart ------------------------------- */

export interface BarChartItem {
  key: string;
  label: string;
  value: number;
  color: string;
}

/** 1 / 2 / 5 / 10 × a power of ten — steps people read without thinking. */
const niceStep = (rough: number) => {
  const power = Math.pow(10, Math.floor(Math.log10(Math.max(rough, 0.0001))));
  const norm = rough / power;
  return (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * power;
};

/** Single-series columns with a value axis, for "how many, exactly". */
export const BarChart = ({ items }: { items: BarChartItem[] }) => {
  const peak = Math.max(1, ...items.map((item) => item.value));
  const step = niceStep(peak / 4);
  const top = Math.ceil(peak / step) * step;
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);

  return (
    <div className="bar-chart">
      <div className="bar-chart__axis">
        {[...ticks].reverse().map((tick) => (
          <span className="bar-chart__tick" key={tick}>
            {tick.toLocaleString()}
          </span>
        ))}
      </div>
      <div className="bar-chart__plot">
        {items.map((item) => (
          <div className="bar-chart__col" key={item.key}>
            <div className="bar-chart__bar-wrap">
              <div
                className="bar-chart__bar"
                style={{
                  height: `${(item.value / top) * 100}%`,
                  background: item.color,
                }}
                title={`${item.label}: ${item.value}`}
              />
            </div>
            <span className="bar-chart__label" title={item.label}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* -------------------------------- UnitGrid ------------------------------- */

export interface UnitGroup {
  key: string;
  value: number;
  color: string;
}

/**
 * One square per thing counted. The form for small whole numbers, where a ring
 * rounds the interesting part away — one owner in a team of eighteen is a 2px
 * slice of a circle but unmistakably one square here.
 */
export const UnitGrid = ({ groups }: { groups: UnitGroup[] }) => {
  const total = groups.reduce((sum, group) => sum + group.value, 0);
  const size = total <= 60 ? 14 : total <= 150 ? 10 : 7;

  return (
    <div className="unit-grid">
      {groups.flatMap((group) =>
        Array.from({ length: group.value }, (_, index) => (
          <span
            className="unit-grid__cell"
            key={`${group.key}-${index}`}
            style={{ width: size, height: size, background: group.color }}
          />
        )),
      )}
    </div>
  );
};
